import { useState, useEffect, useCallback } from "react";
import { Edit2, CheckCircle, XCircle, Clock, Trash2 } from "lucide-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import api, { getAllUsers, approveUser, rejectUser } from "../../../services/api";
import { DataTable } from "../Table";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import UserForm from "./UserForm";
import MmTaskTypesModal from "../MmTaskTypesView/MmTaskTypesModal";
import { useNotification } from "../../../hooks/useNotification";
import { selectUser } from "../../../redux/slices/authSlice";
import { ROLES, normalizeRole } from "../../../constants/roles";
import {
  parseUserCreationError,
  parseUserUpdateError,
  parseUserDeletionError,
  parseUserApprovalError,
  parseUserRejectionError
} from "../../../utils/errorHandler";
import { localizeApiError } from "../../../utils/i18nErrorMap";
import "./UsersView.css";

const DEFAULT_PAGINATION = {
  limit: 10,
  offset: 0,
  total: 0,
};
const DEFAULT_SERVER_SORT = {
  key: "created_at",
  direction: "desc",
};

export default function UsersView({
  searchIntent = null,
  onSearchIntentHandled = null,
}) {
  const { t } = useTranslation();
  const { showSuccess: notifySuccess, showError: notifyError, showWarning } = useNotification();
  const currentUser = useSelector(selectUser);
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [pendingMerchandiserCount, setPendingMerchandiserCount] = useState(0);
  const [showMmTaskTypesModal, setShowMmTaskTypesModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [serverSort, setServerSort] = useState(DEFAULT_SERVER_SORT);
  
  // Modal states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const canManageUser = useCallback((user) => user?.role !== "super_admin", []);

  // Data fetching
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        setError(t("admin.common.notAuthenticated"));
        setLoading(false);
        return;
      }

      const filters = {};
      if (roleFilter !== "all") {
        filters.role = roleFilter;
      }
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }
      filters.limit = pagination.limit;
      filters.offset = pagination.offset;
      filters.sortBy = serverSort.key;
      filters.sortDirection = serverSort.direction;

      const response = await getAllUsers(filters);
      setUsers(response.data || []);
      setPagination((previous) => ({
        ...previous,
        total: response?.pagination?.total || 0,
        limit: response?.pagination?.limit || previous.limit,
        offset: response?.pagination?.offset ?? previous.offset,
      }));
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t("admin.common.authFailed"));
      } else if (err.response?.status === 403) {
        setError(t("admin.common.accessDenied"));
      } else {
        setError(localizeApiError(t, err, "admin.common.failedToLoadUsers"));
      }
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    roleFilter,
    t,
    searchQuery,
    pagination.limit,
    pagination.offset,
    serverSort.key,
    serverSort.direction,
  ]);

  // Fixed: Use statusFilter directly as dependency to prevent duplicate calls
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]); // Use fetchUsers which includes statusFilter in its dependencies

  // Badge count for the merchandiser access-request area. Refreshed whenever
  // the user list changes so approvals immediately drop the count.
  useEffect(() => {
    let cancelled = false;

    const loadPendingMerchandiserCount = async () => {
      try {
        const response = await getAllUsers({
          role: ROLES.MERCHANDISER,
          status: "pending",
          limit: 1,
          offset: 0,
        });
        if (!cancelled) {
          setPendingMerchandiserCount(response?.pagination?.total || 0);
        }
      } catch (err) {
        // Non-critical: the badge simply stays hidden if this fails.
        if (!cancelled) setPendingMerchandiserCount(0);
      }
    };

    loadPendingMerchandiserCount();
    return () => {
      cancelled = true;
    };
  }, [users]);

  useEffect(() => {
    if (!searchIntent) return;

    const nextQuery = String(
      searchIntent?.item?.label ||
      searchIntent?.item?.email ||
      searchIntent?.query ||
      "",
    ).trim();

    setStatusFilter("all");
    setSearchQuery(nextQuery);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));

    if (typeof onSearchIntentHandled === "function" && searchIntent.intentId) {
      onSearchIntentHandled(searchIntent.intentId);
    }
  }, [searchIntent, onSearchIntentHandled]);

  const handleStatusFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  }, []);

  const handleRoleFilterChange = useCallback((value) => {
    setRoleFilter(value);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  }, []);

  // Jump straight to merchandisers awaiting system access.
  const showMerchandiserRequests = useCallback(() => {
    setRoleFilter(ROLES.MERCHANDISER);
    setStatusFilter("pending");
    setSearchQuery("");
    setPagination((previous) => ({ ...previous, offset: 0 }));
  }, []);

  const handleUserSearchChange = useCallback((value) => {
    setSearchQuery(value);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  }, []);

  const handleServerPaginationChange = useCallback(({ limit, offset }) => {
    setPagination((previous) => ({
      ...previous,
      limit,
      offset,
    }));
  }, []);

  const handleServerSortChange = useCallback(({ key, direction }) => {
    const hasValidSort = Boolean(key) && Boolean(direction);
    const normalizedDirection = String(direction || "").toLowerCase();
    const nextSort = hasValidSort && (normalizedDirection === "asc" || normalizedDirection === "desc")
      ? { key, direction: normalizedDirection }
      : DEFAULT_SERVER_SORT;

    setServerSort(nextSort);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  }, []);

  // Action handlers - Approve
  const handleApproveClick = (user) => {
    if (!canManageUser(user)) {
      showWarning(t("admin.users.superAdminProtectedApproval"));
      return;
    }
    setSelectedUser(user);
    setShowApproveConfirmModal(true);
  };

  const handleApproveConfirm = async () => {
    setShowApproveConfirmModal(false);
    setIsActionLoading(true);
    
    // OPTIMISTIC UPDATE: Update UI immediately
    const originalUsers = [...users];
    const updatedUsers = users.map(user => 
      user.user_id === selectedUser.user_id 
        ? { 
            ...user, 
            approval_status: 'approved',
            approvalStatus: { code: 'approved', label: 'Approved' },
            is_active: true // Set user as active
          }
        : user
    );
    setUsers(updatedUsers);
    
    try {
      // Make API call in background
      await approveUser(selectedUser.user_id);
      
      // Success - show notification but DON'T refetch
      notifySuccess(t("admin.users.approvedSuccess", { name: selectedUser.full_name }));
      setSelectedUser(null);
    } catch (err) {
      // ROLLBACK: Revert to original state on error
      setUsers(originalUsers);
      const errorInfo = parseUserApprovalError(err);
      notifyError(localizeApiError(t, err, "errors.generic"), { details: errorInfo.details });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Action handlers - Reject
  const handleRejectClick = (user) => {
    if (!canManageUser(user)) {
      showWarning(t("admin.users.superAdminProtectedApproval"));
      return;
    }
    setSelectedUser(user);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      notifyError(t("admin.users.provideRejectionReason"));
      return;
    }
    setShowRejectModal(false);
    setIsActionLoading(true);
    
    // OPTIMISTIC UPDATE: Update UI immediately
    const originalUsers = [...users];
    const updatedUsers = users.map(user => 
      user.user_id === selectedUser.user_id 
        ? { 
            ...user, 
            approval_status: 'rejected',
            approvalStatus: { code: 'rejected', label: 'Rejected' },
            rejection_reason: rejectionReason
          }
        : user
    );
    setUsers(updatedUsers);
    
    const userName = selectedUser.full_name;
    const reason = rejectionReason;
    
    try {
      // Make API call in background
      await rejectUser(selectedUser.user_id, reason);
      
      // Success - show notification but DON'T refetch
      notifySuccess(t("admin.users.rejectedSuccess", { name: userName }));
      setSelectedUser(null);
      setRejectionReason("");
    } catch (err) {
      // ROLLBACK: Revert to original state on error
      setUsers(originalUsers);
      const errorInfo = parseUserRejectionError(err);
      notifyError(localizeApiError(t, err, "errors.generic"), { details: errorInfo.details });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Action handlers - Delete
  const handleDeleteClick = (user) => {
    if (!canManageUser(user)) {
      showWarning(t("admin.users.superAdminProtectedDelete"));
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    setIsActionLoading(true);
    
    // OPTIMISTIC UPDATE: Remove from UI immediately
    const originalUsers = [...users];
    const updatedUsers = users.filter(user => user.user_id !== selectedUser.user_id);
    setUsers(updatedUsers);
    
    const userName = selectedUser.full_name;
    
    try {
      // Make API call in background
      await api.delete(`/admin/users/${selectedUser.user_id}`);
      
      // Success - show notification but DON'T refetch
      notifySuccess(t("admin.users.deletedSuccess", { name: userName }));
      setPagination((previous) => ({
        ...previous,
        total: Math.max(0, previous.total - 1),
      }));
      setSelectedUser(null);
    } catch (err) {
      // ROLLBACK: Restore user on error
      setUsers(originalUsers);
      const errorInfo = parseUserDeletionError(err);
      notifyError(localizeApiError(t, err, "errors.generic"), { details: errorInfo.details });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Action handlers - Edit
  const handleEditClick = (user) => {
    if (!canManageUser(user)) {
      showWarning(t("admin.users.superAdminProtectedEdit"));
      return;
    }
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (formData) => {
    setShowEditModal(false);
    setIsActionLoading(true);
    
    // OPTIMISTIC UPDATE: Update UI immediately
    const originalUsers = [...users];
    const updatedUsers = users.map(user => {
      if (user.user_id !== selectedUser.user_id) return user;

      const updated = { ...user, ...formData };

      // Sync approval status with is_active change
      if (formData.is_active === false && user.is_active === true) {
        updated.approvalStatus = { code: 'disabled', label: 'Disabled' };
        updated.approval_status = 'disabled';
      } else if (formData.is_active === true && user.is_active === false) {
        updated.approvalStatus = { code: 'approved', label: 'Approved' };
        updated.approval_status = 'approved';
      }

      return updated;
    });
    setUsers(updatedUsers);
    
    try {
      // Make API call in background
      await api.put(`/admin/users/${selectedUser.user_id}`, formData);
      
      // Success - show notification but DON'T refetch
      notifySuccess(t("admin.users.updatedSuccess", { name: formData.full_name }));
      setSelectedUser(null);
    } catch (err) {
      // ROLLBACK: Revert to original state on error
      setUsers(originalUsers);
      const errorInfo = parseUserUpdateError(err);
      const localizedMessage = localizeApiError(t, err, "errors.generic");
      notifyError(localizedMessage, { details: errorInfo.details });
      throw new Error(localizedMessage);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Action handlers - Add
  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleAddSubmit = async (formData) => {
    try {
      // Make API call first
      const response = await api.post('/admin/users', formData);
      
      // Extract user from response - API returns { success, message, data: user }
      const newUser = response.data.data || response.data;
      
      // Ensure newUser has approvalStatus structure for consistency
      const userWithStatus = {
        ...newUser,
        approvalStatus: newUser.approvalStatus || { code: 'approved', description: 'Approved' }
      };
      
      // Optimistically add to beginning of list
      setUsers(prevUsers => [userWithStatus, ...prevUsers]);
      setPagination((previous) => ({
        ...previous,
        total: previous.total + 1,
      }));
      
      setShowAddModal(false);
      
      // Success notification
      notifySuccess(t("admin.users.addedSuccess", { name: formData.full_name }));
    } catch (err) {
      const errorInfo = parseUserCreationError(err);
      const localizedMessage = localizeApiError(t, err, "errors.generic");
      notifyError(localizedMessage, { details: errorInfo.details });
      throw new Error(localizedMessage);
    }
  };

  // Table configuration
  const columns = [
    { label: t("admin.users.name"), sortKey: "full_name", sortValue: (user) => (user.full_name || '').toLowerCase() },
    { label: t("admin.users.email"), sortKey: "email", sortValue: (user) => (user.email || '').toLowerCase() },
    { label: t("admin.users.role"), sortKey: "role", sortValue: (user) => (user.role || '').toLowerCase(), headerStyle: { textAlign: "center" } },
    { label: t("admin.common.status"), sortKey: "approval_status", sortValue: (user) => {
      const status = user.approvalStatus?.code || user.approval_status || 'unknown';
      // Sort order: pending, approved, disabled, rejected
      const order = { 'pending': 1, 'approved': 2, 'disabled': 3, 'rejected': 4, 'unknown': 5 };
      return order[status] || 999;
    }, headerStyle: { textAlign: "center" } },
    { label: t("admin.users.registered"), sortKey: "created_at", sortValue: (user) => user.created_at ? new Date(user.created_at).getTime() : 0, headerStyle: { textAlign: "center" } },
    { label: t("admin.users.actions"), headerStyle: { textAlign: "center" } },
  ];

  const getTableTitle = () => {
    const titles = {
      pending: t("admin.users.pendingApproval"),
      approved: t("admin.users.activeUsers"),
      rejected: t("admin.users.rejectedUsers"),
      disabled: t("admin.users.disabledUsers"),
      all: t("admin.users.allUsers"),
    };
    return titles[statusFilter] || t("admin.users.allUsers");
  };

  const renderUserRow = (user) => (
    <UserTableRow
      key={user.user_id}
      user={user}
      canManage={canManageUser(user)}
      onEdit={handleEditClick}
      onApprove={handleApproveClick}
      onReject={handleRejectClick}
      onDelete={handleDeleteClick}
    />
  );

    return (
    <div className="admin-users-content">
      <div className="admin-users-header">
        <div className="admin-users-header-copy">
          <h2 className="admin-section-title">{t("admin.users.title")}</h2>
          <p className="admin-section-subtitle">{t("admin.users.subtitle")}</p>
        </div>
        <div className="admin-users-header-actions">
          <label className="admin-users-role-filter">
            <span className="admin-users-role-filter-label">
              {t("admin.users.roleFilter")}
            </span>
            <select
              className="admin-users-role-filter-select"
              value={roleFilter}
              onChange={(event) => handleRoleFilterChange(event.target.value)}
            >
              <option value="all">{t("admin.users.allRoles")}</option>
              <option value={ROLES.PROMOTER}>{t("admin.userForm.promoter")}</option>
              <option value={ROLES.MERCHANDISER}>
                {t("admin.userForm.merchandiser")}
              </option>
              {isSuperAdmin && (
                <option value={ROLES.ADMIN}>{t("admin.userForm.admin")}</option>
              )}
            </select>
          </label>
          <button
            className="admin-btn-secondary admin-btn-manage-mm-tasks"
            onClick={() => setShowMmTaskTypesModal(true)}
          >
            {t("admin.mmTasks.manageButton", { defaultValue: "Manage MM Tasks" })}
          </button>
          <button className="admin-btn-primary admin-btn-add-user" onClick={handleAddClick}>
            {t("admin.users.addUser")}
          </button>
        </div>
      </div>

      <MmTaskTypesModal
        isOpen={showMmTaskTypesModal}
        onClose={() => setShowMmTaskTypesModal(false)}
        canEdit={isSuperAdmin}
      />

      {pendingMerchandiserCount > 0 && (
        <button
          type="button"
          className="admin-users-mm-requests"
          onClick={showMerchandiserRequests}
        >
          <span className="admin-users-mm-requests-badge">
            {pendingMerchandiserCount}
          </span>
          <span className="admin-users-mm-requests-text">
            {t("admin.users.merchandiserAccessRequests", {
              count: pendingMerchandiserCount,
            })}
          </span>
          <span className="admin-users-mm-requests-cta">
            {t("admin.users.reviewRequests")}
          </span>
        </button>
      )}

      {/* DataTable with integrated filter, search, pagination, items per page */}
      <DataTable
        data={users}
        columns={columns}
        renderRow={renderUserRow}
        searchFields={["full_name", "email", "role"]}
        searchPlaceholder={t("admin.common.searchByNameEmailRole")}
        title={getTableTitle()}
        loading={loading || isActionLoading}
        error={error}
        emptyMessage={t("admin.users.noUsersFound")}
        initialItemsPerPage={DEFAULT_PAGINATION.limit}
        defaultSortKey="created_at"
        defaultSortDirection="desc"
        showFilter={true}
        filterValue={statusFilter}
        onFilterChange={handleStatusFilterChange}
        filterLabel={t("admin.common.status")}
        filterOptions={[
          { value: 'all', label: t("admin.users.allUsers") },
          { value: 'pending', label: t("admin.common.pending") },
          { value: 'approved', label: t("admin.common.active") },
          { value: 'disabled', label: t("admin.common.disabled") },
          { value: 'rejected', label: t("admin.common.rejected") },
        ]}
        serverMode
        serverSort={serverSort}
        onServerSortChange={handleServerSortChange}
        serverPagination={{
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
        }}
        onServerPaginationChange={handleServerPaginationChange}
        externalSearchQuery={searchQuery}
        onExternalSearchChange={handleUserSearchChange}
        containerClassName="admin-users-section admin-users-table-container"
      />

      {/* Rejection Modal with ConfirmDialog */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        size="medium"
      >
        <div className="admin-users-reject-modal">
          <h3 className="admin-users-reject-modal-title">{t("admin.users.rejectTitle")}</h3>
          <p className="admin-users-reject-modal-line">
            {t("admin.users.userLabel")} <strong>{selectedUser?.full_name}</strong>
          </p>
          <p className="admin-users-reject-modal-line admin-users-reject-modal-line-last">
            {t("admin.users.emailLabel")} <strong>{selectedUser?.email}</strong>
          </p>

          <label className="admin-users-reject-modal-label">
            {t("admin.users.reasonForRejection")}
          </label>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder={t("admin.users.reasonPlaceholder")}
            rows="4"
            className="admin-users-reject-modal-textarea"
          />

          <div className="admin-users-reject-modal-actions">
            <button
              className="admin-btn-secondary"
              onClick={() => setShowRejectModal(false)}
              disabled={isActionLoading}
            >
              {t("admin.common.cancel")}
            </button>
            <button
              className="admin-btn-danger"
              onClick={handleRejectSubmit}
              disabled={isActionLoading}
            >
              {isActionLoading ? t("admin.users.rejecting") : t("admin.users.rejectUser")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.users.deleteTitle")}
          message={t("admin.users.deleteMessage", { name: selectedUser?.full_name || "" })}
          variant="danger"
          confirmText={t("admin.common.delete")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={isActionLoading}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="medium"
      >
        <UserForm
          mode="edit"
          user={selectedUser}
          allowedRoles={
            isSuperAdmin
              ? [ROLES.PROMOTER, ROLES.MERCHANDISER, ROLES.ADMIN]
              : [ROLES.PROMOTER, ROLES.MERCHANDISER]
          }
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        size="medium"
      >
        <UserForm
          mode="add"
          allowedRoles={
            isSuperAdmin
              ? [ROLES.PROMOTER, ROLES.MERCHANDISER, ROLES.ADMIN]
              : [ROLES.PROMOTER, ROLES.MERCHANDISER]
          }
          onSubmit={handleAddSubmit}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal
        isOpen={showApproveConfirmModal}
        onClose={() => !isActionLoading && setShowApproveConfirmModal(false)}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.users.approveTitle")}
          message={t("admin.users.approveMessage", { name: selectedUser?.full_name || "" })}
          variant="success"
          confirmText={t("admin.common.approve")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleApproveConfirm}
          onCancel={() => setShowApproveConfirmModal(false)}
          isLoading={isActionLoading}
        />
      </Modal>
    </div>
  );
}

// --- Child Components ---

function UserTableRow({ user, canManage, onEdit, onApprove, onReject, onDelete }) {
  const { t } = useTranslation();
  const approvalStatus = user.approvalStatus?.code || user.approval_status || "unknown";
  const isPending = approvalStatus === "pending" && canManage;
  const roleLabelMap = {
    promoter: t("admin.userForm.promoter"),
    merchandiser: t("admin.userForm.merchandiser"),
    admin: t("admin.userForm.admin"),
    super_admin: t("admin.sidebar.superAdmin"),
  };
  const capitalizedRole = roleLabelMap[normalizeRole(user.role)] || (user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "");

  return (
    <tr>
      <td>
        <div className="admin-user-cell">
          <UserAvatar name={user.full_name} />
          <span className="admin-user-name">{user.full_name}</span>
        </div>
      </td>
      <td className="admin-user-email">{user.email}</td>
      <td className="admin-users-cell-centered">{capitalizedRole}</td>
      <td className="admin-users-cell-centered">
        <StatusBadge
          status={approvalStatus}
          code={approvalStatus}
        />
      </td>
      <td className="admin-users-cell-centered">
        {new Date(user.created_at).toLocaleDateString()}
      </td>
      <td className="admin-users-cell-centered">
        <div className="admin-action-buttons">
          {isPending && (
            <>
              <button className="admin-btn-approve" onClick={() => onApprove(user)} title={t("admin.users.titleApprove")}>
                <CheckCircle size={14} /> {t("admin.common.approve")}
              </button>
              <button className="admin-btn-reject" onClick={() => onReject(user)} title={t("admin.users.titleReject")}>
                <XCircle size={14} /> {t("admin.common.reject")}
              </button>
            </>
          )}
          {!isPending && canManage && (
            <>
              <button className="admin-btn-edit" onClick={() => onEdit(user)} title={t("admin.users.titleEdit")}>
                <Edit2 size={14} /> {t("admin.common.edit")}
              </button>
              <button className="admin-btn-delete" onClick={() => onDelete(user)} title={t("admin.users.titleDelete")}>
                <Trash2 size={14} /> {t("admin.common.delete")}
              </button>
            </>
          )}
          {!canManage && <span className="admin-user-email">{t("admin.common.protected")}</span>}
        </div>
      </td>
    </tr>
  );
}

function UserAvatar({ name }) {
  const initial = name?.charAt(0).toUpperCase() || "?";
  return <div className="admin-user-avatar-circle">{initial}</div>;
}

function StatusBadge({ status, code }) {
  const { t } = useTranslation();
  let badgeClass = "status-inactive";
  let icon = null;
  let displayStatus = status;

  switch (code) {
    case "pending":
      badgeClass = "status-pending";
      icon = <Clock size={12} />;
      displayStatus = t("admin.common.pending");
      break;
    case "approved":
      badgeClass = "status-approved";
      icon = <CheckCircle size={12} />;
      displayStatus = t("admin.common.active");
      break;
    case "disabled":
      badgeClass = "status-disabled";
      icon = <XCircle size={12} />;
      displayStatus = t("admin.common.disabled");
      break;
    case "rejected":
      badgeClass = "status-rejected";
      icon = <XCircle size={12} />;
      displayStatus = t("admin.common.rejected");
      break;
    default:
      badgeClass = "status-inactive";
      displayStatus = t("admin.common.unknown");
  }

  return (
    <span className={`status-badge ${badgeClass}`}>
      {icon}
      <span className={`admin-users-status-text ${icon ? "has-icon" : ""}`}>{displayStatus}</span>
    </span>
  );
}
